import { NODES_TRANSFORMER_ID } from './constants';
import { normalizeFontSize } from './normalizeFontSize';
import restrictNumber from './restrictNumber';

const CORNER_ANCHORS = new Set([
  'top-left',
  'top-right',
  'bottom-left',
  'bottom-right',
]);

const LETTER_SPACING_MIN = 0;
const LETTER_SPACING_MAX = 15;

const getActiveTransformerAnchor = (node) => {
  const stage = node?.getStage?.();
  if (!stage) {
    return null;
  }
  const transformer = stage.findOne(`#${NODES_TRANSFORMER_ID}`);
  return transformer?.getActiveAnchor?.() ?? null;
};

export const isCornerTransformAnchor = (anchor) =>
  anchor != null && CORNER_ANCHORS.has(anchor);

const scaleOptionalNumber = (value, factor, min, max) => {
  if (value == null || value === '' || Number.isNaN(Number(value))) {
    return undefined;
  }
  return restrictNumber(Number(value) * factor, min, max);
};

/**
 * Build annotation patch from a Konva Text node after transform.
 * Corner handles scale typography + box uniformly (Figma-style).
 * Side handles resize the box only (wrap width / height).
 */
const getTextTransformPropsFromKonva = (node, annotation = {}) => {
  const scaleX = node.scaleX();
  const scaleY = node.scaleY();
  const anchor = getActiveTransformerAnchor(node);
  const cornerScale = isCornerTransformAnchor(anchor);

  const transformProps = {
    id: node.id(),
    rotation: node.rotation(),
    x: node.x(),
    y: node.y(),
    scaleX: 1,
    scaleY: 1,
  };

  if (cornerScale) {
    const uniformScale = Math.min(Math.abs(scaleX), Math.abs(scaleY));
    transformProps.width = node.width() * uniformScale;
    transformProps.height = node.height() * uniformScale;

    const baseFontSize = annotation.fontSize ?? node.fontSize?.() ?? 14;
    transformProps.fontSize = normalizeFontSize(baseFontSize * uniformScale);

    const letterSpacing = scaleOptionalNumber(
      annotation.letterSpacing,
      uniformScale,
      LETTER_SPACING_MIN,
      LETTER_SPACING_MAX,
    );
    if (letterSpacing !== undefined) {
      transformProps.letterSpacing = letterSpacing;
    }

    const strokeWidth = scaleOptionalNumber(
      annotation.strokeWidth,
      uniformScale,
      0,
      100,
    );
    if (strokeWidth !== undefined) {
      transformProps.strokeWidth = strokeWidth;
    }

    const shadowBlur = scaleOptionalNumber(
      annotation.shadowBlur,
      uniformScale,
      0,
      100,
    );
    if (shadowBlur !== undefined) {
      transformProps.shadowBlur = shadowBlur;
    }

    const shadowOffsetX = scaleOptionalNumber(
      annotation.shadowOffsetX,
      uniformScale,
      -100,
      100,
    );
    if (shadowOffsetX !== undefined) {
      transformProps.shadowOffsetX = shadowOffsetX;
    }

    const shadowOffsetY = scaleOptionalNumber(
      annotation.shadowOffsetY,
      uniformScale,
      -100,
      100,
    );
    if (shadowOffsetY !== undefined) {
      transformProps.shadowOffsetY = shadowOffsetY;
    }

    return transformProps;
  }

  transformProps.width = node.width() * Math.abs(scaleX);
  transformProps.height = node.height() * Math.abs(scaleY);
  return transformProps;
};

export default getTextTransformPropsFromKonva;
